/**
 * RinviiPage — gestione rinvii Serie A + 6 politico (FASE 4 iter 7).
 *
 * Per ogni squadra SA rinviata, admin sceglie:
 * - "Rinvia giornata fanta": i giocatori restano SV in attesa di recupero
 * - "Sei politico": applica voto fisso (default 6.0 dal regolamento)
 *
 * Mostra anche eventuali conflitti (voto Gazzetta arrivato DOPO 6 politico).
 */

import { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useStagioneAttiva } from "../../hooks/useStagione";
import { GiornataSelector } from "../common/GiornataSelector";
import {
  DecisioneRinvio,
  useConflittiSeiPolitico,
  useRegistraRinvio,
  useRinvii,
} from "../../hooks/useFase4";
import { MobileShell } from "../shell/MobileShell";
import { localeCorrente } from "../../i18n";

const DECISIONE_LABEL: Record<DecisioneRinvio, string> = {
  rinvia_giornata_fanta: "Rinvia giornata",
  sei_politico: "6 politico",
};

const DECISIONE_COLOR: Record<DecisioneRinvio, string> = {
  rinvia_giornata_fanta: "bg-warning/20 text-warning",
  sei_politico: "bg-info/20 text-info",
};

export function RinviiPage() {
  useAuth(); // session check
  const { data: stagioneAttiva } = useStagioneAttiva();
  const stagioneId = stagioneAttiva?.id ?? null;
  const [giornataId, setGiornataId] = useState("");
  const [squadraSA, setSquadraSA] = useState("");
  const [decisione, setDecisione] = useState<DecisioneRinvio>("sei_politico");
  const [motivazione, setMotivazione] = useState("");
  const [applicaSeiPolitico, setApplicaSeiPolitico] = useState(true);
  const [votoOverride, setVotoOverride] = useState("");
  const [errore, setErrore] = useState<string | null>(null);

  const { data: rinvii } = useRinvii(giornataId || null);
  const { data: conflitti } = useConflittiSeiPolitico(giornataId || null);
  const registraMut = useRegistraRinvio();

  async function submit() {
    if (!giornataId || !squadraSA) return;
    setErrore(null);
    try {
      await registraMut.mutateAsync({
        giornataId,
        payload: {
          squadra_serie_a: squadraSA.trim(),
          decisione,
          motivazione: motivazione.trim() || null,
          applica_sei_politico: decisione === "sei_politico" && applicaSeiPolitico,
          voto_override: votoOverride.trim() || null,
        },
      });
      setSquadraSA("");
      setMotivazione("");
      setVotoOverride("");
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setErrore(err.response?.data?.detail ?? err.message ?? "errore");
    }
  }

  return (
    <MobileShell logoSuffix="RINVII">
      <h2 className="font-display text-3xl text-accent tracking-wide">Rinvii Serie A</h2>

      <div className="card">
        <GiornataSelector stagioneId={stagioneId} value={giornataId} onChange={setGiornataId} />
      </div>

      {giornataId && (
        <>
          <div className="card space-y-3">
            <h3 className="font-bold text-accent">Aggiungi decisione</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                type="text"
                value={squadraSA}
                onChange={(e) => setSquadraSA(e.target.value)}
                placeholder="Nome club Serie A (es. Roma)"
                className="px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <select
                value={decisione}
                onChange={(e) => setDecisione(e.target.value as DecisioneRinvio)}
                className="px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="sei_politico">6 politico</option>
                <option value="rinvia_giornata_fanta">Rinvia giornata fanta</option>
              </select>
            </div>
            <textarea
              value={motivazione}
              onChange={(e) => setMotivazione(e.target.value)}
              rows={2}
              placeholder="Motivazione (opzionale)"
              className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
            {decisione === "sei_politico" && (
              <>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={applicaSeiPolitico}
                    onChange={(e) => setApplicaSeiPolitico(e.target.checked)}
                  />
                  <span>Applica subito i voti 6 politico ai giocatori della squadra</span>
                </label>
                {applicaSeiPolitico && (
                  <input
                    type="text"
                    value={votoOverride}
                    onChange={(e) => setVotoOverride(e.target.value)}
                    placeholder="Voto override (vuoto = default da regolamento)"
                    className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                )}
              </>
            )}
            {errore && <div className="text-sm text-danger">{errore}</div>}
            <button
              onClick={submit}
              disabled={!squadraSA || registraMut.isPending}
              className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {registraMut.isPending ? "…" : "Registra decisione"}
            </button>
          </div>

          <div className="card p-0 overflow-hidden">
            <div className="bg-primary-deep/40 px-4 py-2 text-xs font-bold uppercase tracking-wider">
              Decisioni registrate
            </div>
            {rinvii && rinvii.length === 0 && (
              <div className="p-6 text-center text-fg-muted">
                Nessuna decisione per questa giornata.
              </div>
            )}
            {rinvii?.map((r) => (
              <div
                key={r.id}
                className="flex items-center gap-3 p-4 border-b border-primary/10 last:border-b-0"
              >
                <div className="flex-1">
                  <div className="font-bold">{r.squadra_serie_a}</div>
                  {r.motivazione && (
                    <div className="text-xs text-fg-muted mt-1">{r.motivazione}</div>
                  )}
                </div>
                <span className={`pill ${DECISIONE_COLOR[r.decisione]}`}>
                  {DECISIONE_LABEL[r.decisione]}
                </span>
              </div>
            ))}
          </div>

          {conflitti && conflitti.length > 0 && (
            <div className="card bg-warning/10 border-warning/30">
              <h3 className="font-bold text-warning mb-2">⚠️ Conflitti 6 politico ↔ Gazzetta</h3>
              <p className="text-xs text-fg-muted mb-3">
                Questi giocatori hanno ricevuto un voto Gazzetta DOPO il 6 politico. Il voto più
                recente (Gazzetta) è quello attivo; rivedi se è il comportamento atteso.
              </p>
              <ul className="space-y-2 text-sm">
                {conflitti.map((c) => (
                  <li key={c.giocatore_id} className="border-l-2 border-warning/40 pl-3">
                    <div className="font-bold">{c.giocatore_nome}</div>
                    <div className="text-xs text-fg-muted font-mono">
                      6 politico {new Date(c.voto_6_politico_at).toLocaleString(localeCorrente())} →
                      Gazzetta {new Date(c.voto_gazzetta_at).toLocaleString(localeCorrente())}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </MobileShell>
  );
}
